<template>
  <VaDropdown
    v-model="isDropdownOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge :key="unreadNotifications" overlap>
          <template v-if="unreadNotifications > 0" #text>{{ notificationsBadge }}</template>
          <VaIconNotification v-if="unreadNotifications > 0" class="notification-dropdown__icon" />
          <VaIconNotificationEmpty v-if="unreadNotifications < 1" class="notification-dropdown__icon" />
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <VaList class="space-y-1 mb-2 -mt-1">
          <template v-for="item in notificationsWithRelativeTime" :key="item.id">
            <VaListItem
              :data-id="item.id"
              :class="[
                'relative text-base rounded-xl p-2 va-list-item',
                item.route != '' && item.route != null
                  ? 'cursor-pointer hover:bg-[--va-background-element]'
                  : 'cursor-default',
              ]"
              @click="clickNotification(item)"
            >
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon :name="item.icon" :color="item.isRead ? 'secondary' : 'primary'" />
              </VaListItemSection>
              <VaListItemSection>
                <div :class="['notif-content', item.isRead && 'is-read']">
                  <div class="notif-message">
                    {{ item.message }}
                  </div>

                  <div v-if="item.preview" class="notif-preview">
                    {{ '| ' + item.preview }}
                  </div>
                </div>
              </VaListItemSection>
              <VaListItemSection icon class="mx-1 mr-5 text-gray-400">
                {{ item.updateTimestamp }}
              </VaListItemSection>
              <!-- <VaButton
                  preset="danger"
                  color="danger"
                  class="absolute -top-1.5 -right-1.5 w-6 h-6 p-0 text-xs leading-none min-h-0 min-w-0"
                  @click="dismissNotification(item)"
                >
                  x
                </VaButton> -->
            </VaListItem>
            <VaListSeparator class="w-full m-0" />
          </template>
        </VaList>

        <template v-if="notifications.length < 1"> You're all caught up. </template>
        <VaButton
          v-if="notifications.length > baseNumberOfVisibleNotifications"
          preset="primary"
          class="w-full"
          @click="displayAllNotifications = !displayAllNotifications"
          >{{ displayAllNotifications ? t('notifications.less') : t('notifications.all') }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { Notification } from '@/common/data/entities/Notification'
import { authedRequest } from '@/common/services/api/requests'
import { useUserStore } from '@/common/stores/user-store'
import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { VaIcon, useToast } from 'vuestic-ui'

import VaIconNotification from '../../../icons/VaIconNotification.vue'
import VaIconNotificationEmpty from '../../../icons/VaIconNotificationEmpty.vue'

const { t, locale } = useI18n()
const store = useUserStore()
const router = useRouter()
const { init } = useToast()

const baseNumberOfVisibleNotifications = 4
const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })
const displayAllNotifications = ref(false)
const isDropdownOpen = ref(false)
let observer: IntersectionObserver | null = null

const notificationsBadge = computed(() =>
  unreadNotifications.value > 9 ? '9+' : unreadNotifications.value > 0 ? unreadNotifications.value : null,
)

const toastedIds = new Set<string>()
const notifications = ref<Notification[]>([])
const unreadNotifications = ref(0)

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

let pollTimeout: number | null = null
const SLEEP_SECONDS = 30
const NOTIFICATION_DURATION_SECONDS = 3

document.addEventListener('visibilitychange', async () => {
  if (!document.hidden) {
    await fetchNotifications()
    startPolling()
  } else {
    stopPolling()
  }
})

// delete notifications
// const dismissNotification = async (id: string) => {
//   await authedRequest('BaseBiz', `/Notification/Delete?id=${id}`, 'DELETE')
//   notifications.value = notifications.value.filter((x) => x.id !== id)
// }

const startPolling = () => {
  if (pollTimeout !== null) return

  const tick = async () => {
    if (document.hidden) {
      pollTimeout = null
      return
    }

    await fetchNotifications()

    pollTimeout = window.setTimeout(tick, SLEEP_SECONDS * 1000)
  }

  tick()
}

const stopPolling = () => {
  if (pollTimeout !== null) {
    clearTimeout(pollTimeout)
    pollTimeout = null
  }
}

const clickNotification = async (item: Notification) => {
  if (item.route != '' && item.route != null) {
    if (item.routeId != '' && item.routeId != null) {
      const query: Record<string, string> = { id: item.routeId }
      router.push({ path: item.route, query: query })
      return
    }
    router.push({ path: item.route })
  }
}

const readNotification = async (entity: Notification) => {
  if (entity.isRead) return

  await sleep(3000)

  entity.isRead = true
  // console.log("reading notification: ", entity)
  await authedRequest('BaseBiz', '/Notification/Update', 'PUT', entity)
  unreadNotifications.value = Math.max(0, unreadNotifications.value - 1)
}

const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const fetchNotifications = async () => {
  if (store.userId !== '') {
    const response = await authedRequest('BaseBiz', '/Notification/List', 'POST', {
      fieldValues: { userId: [store.userId] },
    })

    notifications.value = response.entity.sort(
      (a: Notification, b: Notification) => new Date(b.createdAt!).getTime() - new Date(a.createdAt!).getTime(),
    )
    unreadNotifications.value = notifications.value.filter((x) => !x.isRead).length
  }
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const list = displayAllNotifications.value
    ? notifications.value
    : notifications.value.slice(0, baseNumberOfVisibleNotifications)

  return list.map((item, index) => {
    const now = Date.now()
    const ts = new Date((item.createdAt as Date) + 'Z').getTime()

    // clamp future timestamps
    const diff = Math.max(0, now - ts)
    const timeDifference = Math.round(diff)
    const timeName = getTimeName(timeDifference)

    let separator = false

    const nextItem = list[index + 1]
    if (nextItem) {
      const nextItemDifference = Math.round(new Date().getTime() - new Date(nextItem.createdAt as Date).getTime())
      const nextItemTimeName = getTimeName(nextItemDifference)

      if (timeName !== nextItemTimeName) {
        separator = true
      }
    }

    return {
      ...item,
      updateTimestamp:
        timeDifference === 0
          ? 'just now'
          : rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName).replace('ago', ''),
      separator,
    }
  })
})

const observeNotifications = async () => {
  await nextTick()

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = (entry.target as HTMLElement).dataset.id
            const notification = notifications.value.find((n) => n.id === id)
            if (notification && !notification.isRead) {
              readNotification(notification)
              observer!.unobserve(entry.target)
            }
          }
        })
      },
      { threshold: 0.5 },
    ) // triggers when half visible
  }

  const elements = document.querySelectorAll('.va-list-item')
  elements.forEach((el) => observer!.observe(el))
}

watch(displayAllNotifications, async (expanded) => {
  if (expanded && isDropdownOpen.value) {
    await observeNotifications()
  }
})

watch(isDropdownOpen, async (open) => {
  if (!open) return
  await observeNotifications()
})

watch(isDropdownOpen, (open) => {
  if (!open && observer) {
    observer.disconnect()
  }
})

watch(notifications, async () => {
  if (isDropdownOpen.value) {
    await observeNotifications()
  }
})

watch(notifications, async (list) => {
  for (const notification of list) {
    if (!notification.isRead && !toastedIds.has(notification.id!)) {
      toastedIds.add(notification.id!)

      await sleep(NOTIFICATION_DURATION_SECONDS * 1000 + 500) // slight delay to avoid buggy overlapping notifications

      init({
        message: '',
        color: 'info',
        duration: NOTIFICATION_DURATION_SECONDS * 1000,
        render: () =>
          h('div', { class: 'flex items-start space-x-2' }, [
            h(VaIcon, { name: notification.icon, class: 'mt-0.5' }),
            h('div', [
              h('div', { class: 'font-semibold' }, notification.message),
              notification.preview ? h('div', { class: 'text-sm opacity-70' }, notification.preview) : null,
            ]),
          ]),
      })
    }
  }
})

onMounted(() => {
  startPolling()
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.notif-message {
  color: var(--va-text-primary);
}

.notif-preview {
  color: var(--va-text-secondary);
  font-size: 0.85em;
  margin-top: 2px;

  opacity: 0.7;
}

.notif-content.is-read {
  opacity: 0.65;
}
</style>
